import React, { useRef } from 'react';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Modal } from 'antd';
import W4FormTemplate from './W4Form/index';
import PayrollDirectDepositTemplate from './PayrollDirectDeposit';
import OnboardingInfoTemplate from './OnboardingInfo';
import EmployeeRightsTemplate from './EmployeeRights';
import I9FormTemplate from './I9Form/Index';

const FormTemplate = ({ openTemplate, setOpenTemplate, templateName, data }) => {
  const templateRef = useRef();
  const [windowWidth, setWindowWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 390
  );

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isDesktop = windowWidth > 768;
  const availableWidth = windowWidth - 24;
  const previewScale = !isDesktop && availableWidth < 359 ? availableWidth / 359 : 1;

  const handleDownload = async () => {
    const input = templateRef.current;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Scale image width → fit PDF
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = pdfWidth / imgWidth;
    const pageHeight = imgHeight * ratio; // scaled total height

    let heightLeft = pageHeight;
    let yOffset = 0;

    // Add first page
    pdf.addImage(imgData, "PNG", 0, yOffset, pdfWidth, pageHeight);
    heightLeft -= pdfHeight;

    // Add remaining pages
    while (heightLeft > 10) {
      yOffset -= pdfHeight; // move image upwards
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, yOffset, pdfWidth, pageHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(`${templateName}.pdf`);
  };

  const getTemplateComponent = () => {
    switch (templateName) {
      case 'W4-Form':
        return W4FormTemplate;
      case 'I9-Form':
        return I9FormTemplate;
      case 'EmployeeRights':
        return EmployeeRightsTemplate;
      case 'PayrollDirectDeposit':
        return PayrollDirectDepositTemplate;
      case 'OnboardingInfo':
        return OnboardingInfoTemplate;

      default:
        return W4FormTemplate;
    }
  };

  const TemplateComponent = getTemplateComponent();

  return (
    <>
      <Modal
        className="form-template-modal"
        width={isDesktop ? 830 : 390}
        open={openTemplate}
        closeIcon={null}
        title={null}
        footer={null}
        maskClosable={true}
        centered
        onCancel={() => setOpenTemplate(false)}
        styles={{
          body: {
            padding: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
          content: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            padding: 0,
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Top Close Button Row */}
          <div
            style={{
              width: isDesktop ? 794 : Math.min(359, availableWidth),
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: 10,
            }}
          >
            <div
              onClick={() => setOpenTemplate(false)}
              role="button"
              tabIndex={0}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                background: '#FFFFFF',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <CloseOutlined style={{ fontSize: 16, color: '#000000' }} />
            </div>
          </div>

          {/* Scaled Preview Sheet */}
          <div
            style={{
              transform: previewScale < 1 ? `scale(${previewScale})` : 'none',
              transformOrigin: 'top center',
              marginBottom: previewScale < 1 ? `-${Math.round((1 - previewScale) * 465)}px` : 0,
            }}
          >
            <TemplateComponent data={data} isDownload={isDesktop} />
          </div>

          {/* Clean Centered Download Button */}
          <div
            style={{
              marginTop: 16,
              marginBottom: 8,
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Button
              type="primary"
              onClick={handleDownload}
              style={{
                borderRadius: 23,
                height: 44,
                minWidth: 160,
                fontSize: 14,
                fontWeight: 600,
                background: '#1677FF',
                boxShadow: '0px 4px 12px rgba(22, 119, 255, 0.35)',
              }}
            >
              Download
            </Button>
          </div>
        </div>
      </Modal>

      {/* For download */}
      <div
        ref={templateRef}
        style={{
          position: "absolute",
          left: "-9999px", // keeps it out of view
          top: 0,
        }}
      >
        <TemplateComponent data={data} isDownload={true} />
      </div>
    </>
  );
};

export default FormTemplate;  